import { hasAnyPermission } from '@solote/contracts';

/**
 * PARA ONDE CADA PESSOA VAI
 *
 * Fica separado dos hooks de propósito: é uma decisão pura, e decisão pura
 * dá para testar sem montar React. As asserções vivem em
 * `scripts/check-permissions.ts`.
 *
 * Corrige uma regressão real: o destino após o login era fixo em `/painel`,
 * a área do ANUNCIANTE. Quem é da equipe interna e não anuncia nada
 * (moderador, financeiro) batia na guarda daquela área e era devolvido para
 * a home, sem explicação nenhuma.
 */

/**
 * Basta UMA destas para o painel administrativo abrir. A tela de dentro é
 * decidida rota a rota — quem só tem `payments.view` entra e enxerga apenas
 * o financeiro.
 */
export const ADMIN_ENTRY_PERMISSIONS = [
  'listings.view',
  'listings.approve',
  'leads.view',
  'payments.view',
  'plans.view',
  'forms.edit',
  'users.view',
  'roles.view',
  'audit.view',
];

/** Papéis que dão acesso à área do anunciante. */
export const ADVERTISER_ROLES = ['advertiser', 'owner', 'broker', 'agency', 'developer'];

/**
 * A ordem importa: quem acumula os dois papéis vai para o painel
 * administrativo, que é onde a pessoa trabalha.
 */
export function resolveHomeRoute(
  user: { roles: readonly string[]; permissions: readonly string[] } | null,
): string {
  if (!user) return '/';
  if (hasAnyPermission(user.permissions, ADMIN_ENTRY_PERMISSIONS)) return '/admin';
  if (ADVERTISER_ROLES.some((role) => user.roles.includes(role))) return '/painel';

  return '/';
}

/**
 * Este destino serve para ESTA pessoa?
 *
 * O "de onde vim" que chega ao login vem de duas origens escritas por
 * terceiros: o estado do roteador, empurrado pelo guardião, e a URL. Nenhuma
 * das duas sabe QUEM vai entrar depois — e foi exatamente isso que quebrou:
 * o anunciante saía de `/painel/anuncios`, o guardião gravava esse destino, e
 * a próxima pessoa a entrar naquela aba era levada para lá. Um administrador
 * caía na área do anunciante.
 *
 * Conferir aqui não substitui o guardião da rota, que continua barrando quem
 * não pode entrar. O que isto evita é o passeio inútil: entrar, ser mandado
 * para uma tela que não é sua, e ser expulso dela no instante seguinte.
 */
export function canReach(
  user: { roles: readonly string[]; permissions: readonly string[] } | null,
  route: string,
): boolean {
  if (!user) return false;

  const caminho = route.split('?')[0] ?? '';

  if (caminho === '/admin' || caminho.startsWith('/admin/')) {
    return hasAnyPermission(user.permissions, ADMIN_ENTRY_PERMISSIONS);
  }

  if (caminho === '/painel' || caminho.startsWith('/painel/')) {
    return ADVERTISER_ROLES.some((role) => user.roles.includes(role));
  }

  // Rota pública: qualquer pessoa autenticada chega.
  return true;
}
